import { memo, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Divider } from 'rsuite';
import { RootState } from '../../../@types/RootState';
import { selectorUser } from '../../../lib/selectorUser';
import { hasResolvedAccessRule } from '../../../utils/ipaAccess';
import { SET_IS_OPEN, SetIsOpenPayload } from '../../../reducers/navBar/modals';
import NavBarMenuContentItemWrapper from '../NavBarMenuContentItem/NavBarMenuContentItemWrapper';
import * as utils from '../utils';
import styles from './NavBarMenuContent.module.scss';

type NavBarMenuContentProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> & {
    handleCloseMenu: () => void;
  };

const NavBarMenuContent = ({ user, clienteDataLoadFinished, handleCloseMenu, setIsOpen }: NavBarMenuContentProps) => {
  const onSelect = useCallback(
    ({ key, isDisabled, hasService }: utils.NavOption) => {
      return () => {
        if (key === 'NEG' && isDisabled) return;

        handleCloseMenu();

        switch (key) {
          case 'PAN': {
            if (isDisabled) {
              setIsOpen({ key, payload: true });
              return;
            } else if (!hasService) {
              setIsOpen({ key: 'none', payload: true });
            }
            break;
          }
          case 'COL': {
            if (isDisabled) {
              setIsOpen({ key, payload: true });
              return;
            } else if (!hasService) {
              setIsOpen({ key: 'none', payload: true });
            }
            break;
          }
          case 'IPA': {
            if (isDisabled) {
              setIsOpen({ key, payload: true });
              return;
            }
            break;
          }
          default:
            break;
        }
      };
    },
    [handleCloseMenu, setIsOpen],
  );

  const hasCustomizedPannels: boolean = useMemo(() => {
    return utils.checkHasCustomizedPannels(user);
  }, [user]);

  const isFree: boolean = useMemo(() => {
    return utils.checkIsFree(user);
  }, [user]);

  const getCustomizedPannelsLink = () => {
    if (user?.servicoBi?.analises?.length) {
      return `/analise/${user?.servicoBi?.analises[0]?.codigo}`;
    }
    return `/acompanhamento/${user?.servicoBi?.graficos?.[0]?.codigo}`;
  };

  if (!hasResolvedAccessRule(user) && !clienteDataLoadFinished) {
    return (
      <div className={styles['wrapper']}>
        <h4 className={styles['title']}>Produtos</h4>
        <ul className={styles['main-menu']} aria-busy="true" data-testid="product-menu-loading" />
      </div>
    );
  }

  return (
    <div className={styles['wrapper']}>
      <h4 className={styles['title']}>Produtos</h4>
      <ul className={styles['main-menu']}>
        {utils
          .navOptions(user)
          .sort(utils.sortByEnabledFirst)
          .map(option => (
            <NavBarMenuContentItemWrapper option={option} onSelect={onSelect} />
          ))}
      </ul>
      {hasCustomizedPannels && (
        <ul className={styles['sub-menu']}>
          <li>
            <Divider className={styles['sub-menu__divider']} />
          </li>
          <li>
            <NavBarMenuContentItemWrapper
              option={{
                key: 'BI',
                id: isFree ? 'custom-panels-navbar-link-free' : 'custom-panels-navbar-link',
                to: getCustomizedPannelsLink(),
                label: 'Painéis Customizados',
              }}
              onSelect={onSelect}
            />
          </li>
        </ul>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  user: selectorUser(state),
  clienteDataLoadFinished: Boolean(state.clienteDataReducer?.clienteDataLoadFinished),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setIsOpen: (payload: SetIsOpenPayload) => dispatch(SET_IS_OPEN(payload)),
});

export default memo(connect(mapStateToProps, mapDispatchToProps)(NavBarMenuContent));
